import { useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { ChevronDown, KeyRound, LogOut } from 'lucide-react';
import useBodyClasses from '@/hooks/useBodyClasses';
import { Container } from '@/components/container';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePortal } from '@/portal/PortalProvider';
import { PortalNavbar } from './PortalNavbar';
import { OakFeaturesDialog, OAK_SITE } from './OakFeaturesDialog';

const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

/**
 * The client portal's shell (OL-192). It copies the staff app's header and top
 * menu so a guest sees what OAK looks like, but it is its own component tree:
 * nothing here loads staff data or holds a staff token.
 */
const PortalLayout = () => {
  // The staff layout's variables, so the header and menu bar match it.
  useBodyClasses(`
    [--tw-page-bg-dark:var(--tw-coal-500)]
    [--tw-header-height:78px]
    [--tw-navbar-bg:var(--tw-secondary-active)]
    [--tw-navbar-bg-dark:var(--tw-coal-300)]
    bg-light
    dark:bg-coal-500
  `);
  const { branding, guest, signOut } = usePortal();
  const [oakFeature, setOakFeature] = useState(null);

  return (
    <div className="flex grow flex-col w-full min-h-screen">
      <header className="flex items-center shrink-0 bg-[--tw-header-bg] dark:bg-coal-500 h-[--tw-header-height]">
        <Container className="flex justify-between items-center gap-2.5 min-w-0">
          <Link to="/portal" className="flex items-center gap-3 min-w-0">
            {branding.logo ? (
              <img src={branding.logo} alt={branding.firm_name ?? 'logo'} className="max-h-[42px] max-w-[160px] object-contain" />
            ) : null}
            <span className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-gray-900 truncate">{branding.firm_name}</span>
              <span className="text-2xs text-gray-500">Client Portal</span>
            </span>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-100">
                <span className="size-9 rounded-full bg-primary-light text-primary flex items-center justify-center text-xs font-semibold">
                  {initials(guest?.name)}
                </span>
                <span className="hidden sm:flex flex-col items-start leading-tight">
                  <span className="text-sm font-medium text-gray-900">{guest?.name}</span>
                  <span className="text-2xs text-gray-500">Guest</span>
                </span>
                <ChevronDown size={14} className="text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex flex-col">
                <span className="text-sm text-gray-900">{guest?.name}</span>
                <span className="text-2xs font-normal text-gray-500 truncate">{guest?.email}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/portal/account" className="flex items-center gap-2">
                  <KeyRound size={14} /> Account
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={signOut} className="flex items-center gap-2">
                <LogOut size={14} /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Container>
      </header>

      <PortalNavbar showOakFeatures={!branding.hide_oak_features} onOakFeature={setOakFeature} />

      <Container className="flex flex-col grow w-full">
        <main className="flex flex-col grow w-full" role="content">
          <Outlet />
        </main>
      </Container>

      <footer className="py-5 mt-8 border-t border-gray-200">
        <Container className="flex flex-wrap justify-between gap-2 text-2xs text-gray-500">
          <span>Documents shared with you by {branding.firm_name}. Every document you open is recorded.</span>
          <a href={OAK_SITE} target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
            Powered by <span className="font-semibold">OAK Legal</span>
          </a>
        </Container>
      </footer>

      {!branding.hide_oak_features && (
        <OakFeaturesDialog feature={oakFeature} firmName={branding.firm_name} onClose={() => setOakFeature(null)} />
      )}
    </div>
  );
};

export { PortalLayout };
